import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";
import { Skeleton } from "components/common/Skeleton";
import { Inline } from "components/common/Stack";
import { Button } from "components/common/Button";
import { OverlayScroll } from "components/common/OverlayScroll";
import { COUNT_TEXT } from "components/common/SectionRule";
import { SpaceDot } from "@/components/main/spaces/SpaceDot";
import { formatCount } from "@/lib/format";

/// How many people a list of them shows before it scrolls.
export const SHOWN_ROWS = 8;

/// The rows and the gaps between them (`space-y-1`).
const listHeight = (rows: number) =>
  `calc(var(--item-height) * ${rows} + var(--spacing-xs) * ${rows - 1})`;

/// A settings roster (`fill`) stops this far above the window's bottom.
const FILL_BOTTOM_GAP_VH = 12;

/// A list of people: `rows` of them, or with `fill` down to the window's floor
/// (a settings page's roster), then it scrolls under a thumb shown on hover.
export const PeopleScroll: React.FC<{
  rows?: number;
  /// Keeps this many rows' height when fewer show, so the list doesn't shrink.
  minRows?: number;
  fill?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ rows = SHOWN_ROWS, minRows, fill = false, className, children }) => {
  const box = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState<number>();
  // Every render: whatever sits above the list (a search, a callout) moves its top.
  useLayoutEffect(() => {
    if (!fill) return;
    const fit = () => {
      const top = box.current?.getBoundingClientRect().top ?? 0;
      const next = window.innerHeight * (1 - FILL_BOTTOM_GAP_VH / 100) - top;
      setRoom(next > 0 ? next : undefined);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  });
  return (
    <OverlayScroll
      scrollRef={box}
      reveal="hover"
      frameClassName={className}
      style={{
        maxHeight: fill ? room : listHeight(rows),
        minHeight: minRows ? listHeight(minRows) : undefined,
      }}
    >
      {children}
    </OverlayScroll>
  );
};

export const LoadingRows: React.FC<{ label: string }> = ({ label }) => (
  <div className="space-y-1">
    <span className="sr-only" role="status">
      {label}
    </span>
    {["40%", "55%", "32%"].map((width) => (
      <div key={width} className="item-height flex items-center pl-2">
        <Skeleton width={width} />
      </div>
    ))}
  </div>
);

export const LoadFailed: React.FC<{
  onRetry?: () => void;
  children: React.ReactNode;
}> = ({ onRetry, children }) => (
  <Inline gap="sm" align="center" className="item-height px-2">
    <span className="text-small text-(--color-error)">{children}</span>
    <Button
      variant="link"
      size="xs"
      className="section-rule-action"
      onClick={onRetry}
    >
      Try again
    </Button>
  </Inline>
);

const peopleNoun = (count: number) => (count === 1 ? "person" : "people");

/// People who hold access through a space, read-only: its dot, name and count
/// heading a dashed block, the people inside.
export const SpaceBlock: React.FC<{
  name: string;
  color: string;
  caption?: string;
  listId?: string;
  open: boolean;
  count?: number;
  children: React.ReactNode;
}> = ({ name, color, caption, listId, open, count, children }) => (
  <div className="rounded-app border border-dashed border-border">
    <div className="flex items-center gap-2 item-height px-3">
      <SpaceDot color={color} />
      <span className="truncate text-fg-medium">{name}</span>
      {caption && (
        <span className="truncate text-fg-muted-regular-small">{caption}</span>
      )}
      {count !== undefined && (
        <span className={`ml-auto shrink-0 ${COUNT_TEXT}`}>
          <span aria-hidden>{formatCount(count)}</span>
          <span className="sr-only">
            {count.toLocaleString("en-US")} {peopleNoun(count)}
          </span>
        </span>
      )}
    </div>
    {open && (
      <div id={listId} className="px-1 pb-1">
        {children}
      </div>
    )}
  </div>
);

interface AccessItemProps {
  primaryText: string;
  tooltipText?: string;
  permissionLabel?: React.ReactNode;
  isCurrentUser?: boolean;
  isPending?: boolean;
  isActive?: boolean;
  /// Beside the name on hover or focus, in place of the name's tooltip.
  email?: string;
  /// Keeps `email` in view, where it may be what a search matched.
  emailShown?: boolean;
  /// Where else they hold access, ahead of the role: "Collaborator directly",
  /// or in search the space's dot.
  source?: React.ReactNode;
  /// Badges about the person ("Guest"), right after the name.
  tags?: React.ReactNode;
}

export const AccessItem: React.FC<AccessItemProps> = ({
  primaryText,
  tooltipText,
  permissionLabel,
  isCurrentUser = false,
  isPending = false,
  isActive = false,
  email,
  emailShown = false,
  source,
  tags,
}) => {
  const content = (
    <span
      className={`truncate block ${isPending ? "text-fg-muted-medium italic" : "text-fg-regular"}`}
    >
      {primaryText}
    </span>
  );

  return (
    <div
      className={email ? "group access-row" : "group"}
      data-active={(email && isActive) || undefined}
    >
      <div
        className={`cursor-default item-height flex items-center gap-2 w-full rounded-app hover:bg-list-hover px-2 ${isActive ? "bg-list-hover" : ""}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {tooltipText && !email ? (
            <Tooltip title={tooltipText} placement="bottom-start">
              {content}
            </Tooltip>
          ) : (
            content
          )}
          {isCurrentUser && (
            <span className="text-fg-semibold shrink-0">(You)</span>
          )}
          {tags && (
            <span className="flex shrink-0 items-center gap-1">{tags}</span>
          )}
          {email && (
            <span
              className="access-row-email truncate min-w-0 shrink-4 text-fg-muted-regular-small"
              data-shown={emailShown || undefined}
            >
              {email}
            </span>
          )}
        </div>
        {source && (
          <span className="text-fg-muted-regular-small shrink-0">{source}</span>
        )}
        {permissionLabel && (
          <div className="flex items-center ml-auto shrink-0">
            {permissionLabel}
          </div>
        )}
      </div>
    </div>
  );
};
